import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import {
    getActiveVacancies
} from "../../../../store/vacancy/thunks";
import JobHeader from "../JobHeader";
import {
  DEFAULT_ITEMS_PER_PAGE
} from "../../../../constants/app";
import {
  AcceptedVacancySortField,
  SortOrder,
  TVacancyDetail,
} from "../../../../store/vacancy/types";
import { STORAGE_KEYS } from "../../../../constants/storageKeys";
import storage from "../../../../utils/storage";
import AcceptedTableHeader from "../../../Client/Vacancies/Accepted/AcceptedTableHeader";
import AcceptedCard from "../../../Client/Vacancies/Accepted/AcceptedCard";
import { getCandidateDataForCentreThunk } from "../../../../store/centers/thunks";
import PaginationControls from "../../../Pagination/PaginationControls";
import ViewProfile from "../../../Client/Candidates/PopupModal/ViewProfile";
import PopupModal from "../../../UI/Popup";
import CancelVacancy from "../../../Client/Vacancies/Popup/CancelVacancy";

const AcceptedVacancy: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedCentreDetails = useSelector(
    (state) => state.vacancy.selectedCentreDetails
  );
  const isShowNotification = useSelector(
    (state) => state.app.isShowNotification
  );
  const {
    acceptedVacancies,
    totalAcceptedVacancies,
    acceptedpagination,
  } = useSelector((state) => state.vacancy.active);

  type ModalType = "RATING" | "CANCEL" | null;
  const [sortBy, setSortBy] = useState<AcceptedVacancySortField>("timeframe");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [dropdownId, setDropdownId] = useState<number | null>(
    null
  );
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [selectedVacancy, setSelectedVacancy] = useState<TVacancyDetail | null>(
    null
  );
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  // Fetch active vacancies (debounced, sorted, paginated)
  const fetchActiveVacancies = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());
    dispatch(
      getActiveVacancies({
        accepted_sort_by: sortBy,
        accepted_sort_order: sortOrder,
        accepted_vacancy_page: page,
        accepted_per_page: perPage,
        centre_uuid:
          selectedCentreDetails !== null
            ? selectedCentreDetails
            : storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID),
      })
    ).finally(() => {
      dispatch(hideTransparentPreloaderAction());
    });
  }, [
    dispatch,
    page,
    perPage,
    sortBy,
    sortOrder,
    selectedCentreDetails,
  ]);

  // Call fetch function on filter/pagination/sort change
  useEffect(() => {
    if(!isShowNotification) {
      fetchActiveVacancies();
    }
  }, [
    fetchActiveVacancies,
    page,
    perPage,
    sortBy,
    sortOrder,
    isShowNotification
  ]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("accepted_vacancy_page");
      setPage(Number(page));
    }
  };

  const handleSortChange = (field: AcceptedVacancySortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleRating = () => {
    setDropdownId(null);
    setActiveModal("RATING");
  };

  const handleViewVacancy = async (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.SELECTED_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.ADMIN_PENDING_VACANCIES);
  };

  const handleDuplicateVacancy = (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.DUPLICATE_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.ADMIN_CREATE_VACANCY);
  };

  const handleCandidateProfileView = async (vacancy: TVacancyDetail) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getCandidateDataForCentreThunk({
        candidate_id: String(vacancy.selected_candidate.uuid),
        centre_id: String(vacancy.centre_id),
      })
    );
    dispatch(hideTransparentPreloaderAction());
    setShowProfileModal(!false);
  };

  const handleCancel = (vacancy: TVacancyDetail) => {
    setDropdownId(null);
    setSelectedVacancy(vacancy);
    setActiveModal("CANCEL");
  };

  const onClickClose = () => {
    setActiveModal(null);
    setSelectedVacancy(null);
    setShowProfileModal(false)
  }

  return (
    <>
      <ViewProfile 
        showProfileModal={showProfileModal}
        onClickClose={onClickClose}
      />

      {/* Cancel Modal */}
      <PopupModal
        visible={!!activeModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
      >
        {/* Cancel Modal */}
        {activeModal === "CANCEL" && selectedVacancy && (
          <CancelVacancy vacancy={selectedVacancy} onClose={onClickClose} />
        )}
      </PopupModal>
      {/* Cancel Modal */}

      <div className="d-flex flex-column gap-20">
        <div className="d-flex flex-column">
          <JobHeader 
            acceptedJobCount={totalAcceptedVacancies}
          />
        </div>
        <div className="d-flex flex-column">
          {/* <div className="bg-white py-10px px-15px d-none d-lg-flex flex-column flex-lg-row gap-10 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400 text-black">
                <div className="flex-grow-1"></div>
                <div className="min-w-300px">
                <div
                    className="fs-13 fw-700 d-flex gap-5 align-items-center cursor-pointer"
                    onClick={() => handleTimeFrameSort("timeframe")}
                >
                    Requested Timeframe
                    <SortBy
                    fill={sortBy === "timeframe" ? "#167E76" : "#E0E0E0"}
                    sort_order={sortBy === "timeframe" ? sortOrder : ""}
                    />
                </div>
                </div>
                <div
                className="min-w-120px d-flex gap-5 align-items-center cursor-pointer"
                onClick={() => handleTimeFrameSort("created_at")}
                >
                <div className="fs-13 fw-700 ">Posted</div>
                <SortBy
                    fill={sortBy === "created_at" ? "#167E76" : "#E0E0E0"}
                    sort_order={sortBy === "created_at" ? sortOrder : ""}
                />
                </div>
                <div className="min-w-200px">
                <div className="fs-13 fw-700">Settings</div>
                </div>
                <div className="min-w-165px"></div>
                <div className="min-w-24px"></div>
            </div> */}
          <AcceptedTableHeader
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />

          {acceptedVacancies && acceptedVacancies?.length > 0 ? (
            acceptedVacancies?.map((item: any, index: number) => {
              return (
                <div key={index}>
                  {/* <VacancyCard vacancy={item} type="accepted" /> */}
                  <AcceptedCard
                    key={item.id}
                    vacancy={item}
                    showDropdown={dropdownId === item.id}
                    setShowDropdown={(val) =>
                      setDropdownId(val ? item.id : null)
                    }
                    onViewDetails={() => handleViewVacancy(item)}
                    handleProfileView={() => handleCandidateProfileView(item)}
                    handlePreferred={() => {}}
                    handleDuplicate={() => handleDuplicateVacancy(item)}
                    handleCancel={() => handleCancel(item)}
                  />
                </div>
              );
            })
          ) : (
            <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
              <div className="fs-16 fw-400 text-grey-medium">
                No Accepted Vacancies Found
              </div>
            </div>
          )}

          {/* Pagination Component */}
          {acceptedpagination &&
            acceptedpagination?.total > DEFAULT_ITEMS_PER_PAGE && (
            <PaginationControls
                currentPage={page} // Current page number
                totalPages={acceptedpagination?.last_page} // Total number of pages
                paginationLinks={acceptedpagination?.links} // List of pagination links
                onPageChange={handlePageChange} // Function to handle page change
                setPerPageList={setPerPage} // Function to set the number of items per page
                setCurrentPage={setPage} // Function to set the current page
            />
            )}
          {/* Pagination Component */}
        </div>
      </div>
    </>
  );
};

export default AcceptedVacancy;
